/**
 * Build metadata surfaced in the UI (login footer + sidebar user section).
 *
 * Three values are injected at build time:
 * - `VITE_BUILD_NUMBER` — CI pipeline run number or short git SHA.
 * - `VITE_COMMIT_TIME` — ISO-8601 timestamp of the deployed commit (committer
 *   date, `git show -s --format=%cI`). This is the *commit* time, not the image
 *   build time, so its age tells you how old the deployed code is.
 * - `VITE_BUILD_TIME` — ISO-8601 timestamp of when CI built the image. Its age
 *   tells you how fresh the deployment is, which can differ from the code age
 *   when the tip of main is older than the last deploy.
 *
 * Both the login footer and the sidebar render the same labels (via
 * `BuildBadge`), so they always show the same values from this single source.
 *
 * When a value is absent — local `vite dev` / `vite build` without the var
 * set — we fall back gracefully so the UI never shows a blank.
 */
const RAW_BUILD_NUMBER = import.meta.env.VITE_BUILD_NUMBER
const RAW_COMMIT_TIME = import.meta.env.VITE_COMMIT_TIME
const RAW_BUILD_TIME = import.meta.env.VITE_BUILD_TIME

/** The resolved build number, e.g. `"1234"`, `"a1b2c3d"`, or `"dev"`. */
export const BUILD_NUMBER: string =
  RAW_BUILD_NUMBER && RAW_BUILD_NUMBER.trim().length > 0 ? RAW_BUILD_NUMBER.trim() : 'dev'

/** Parse an injected ISO-8601 timestamp; `null` when unset / unparseable. */
function parseTime(raw: string | undefined): Date | null {
  if (!raw || raw.trim().length === 0) return null
  const parsed = new Date(raw.trim())
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

/**
 * Format as `M/D HH:mm UTC` (year omitted to stay compact). UTC keeps it
 * unambiguous and stable regardless of the viewer's timezone.
 */
function timeLabel(time: Date): string {
  return `${time.getUTCMonth() + 1}/${time.getUTCDate()} ${time.toISOString().slice(11, 16)} UTC`
}

/** Relative age ("just now", "42m ago", "3h ago", "2d ago") against `now`. */
function ageLabel(time: Date, now: Date): string {
  const seconds = Math.max(0, Math.round((now.getTime() - time.getTime()) / 1000))
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

/** The resolved commit time, or `null` when unset / unparseable. */
export const COMMIT_TIME: Date | null = parseTime(RAW_COMMIT_TIME)

/** The resolved image build time, or `null` when unset / unparseable. */
export const BUILD_TIME: Date | null = parseTime(RAW_BUILD_TIME)

/**
 * Commit time formatted as `M/D HH:mm UTC` (year omitted to stay compact).
 * `null` when no commit time is available.
 */
export const COMMIT_TIME_LABEL: string | null = COMMIT_TIME ? timeLabel(COMMIT_TIME) : null

/**
 * Relative commit age ("just now", "42m ago", "3h ago", "2d ago") — how old the
 * deployed code is — computed against `now` (defaults to the current time).
 * `null` when no commit time. Callers should pass a `now` that updates over
 * time (see `BuildBadge`) so the value doesn't go stale in a long-lived session.
 */
export function commitAgeLabel(now: Date = new Date()): string | null {
  return COMMIT_TIME ? ageLabel(COMMIT_TIME, now) : null
}

/**
 * Display label, e.g. `"a1b2c3d · 6/25 15:42 UTC (3h ago)"` (build number ·
 * commit time · commit age) or, when no commit time is baked in, just the
 * build number (`"dev"`). Pass the current time so the age stays fresh.
 */
export function buildLabel(now: Date = new Date()): string {
  if (!COMMIT_TIME) return BUILD_NUMBER
  return `${BUILD_NUMBER} · ${timeLabel(COMMIT_TIME)} (${ageLabel(COMMIT_TIME, now)})`
}

/**
 * Display label for the image build time, e.g. `"built 7/8 03:12 UTC (5h ago)"`
 * — when the running image was built, i.e. how fresh the deployment is,
 * distinct from how old the deployed code is. `null` when no build time is
 * baked in (local dev); `BuildBadge` omits its second pill then. Pass the
 * current time so the age stays fresh.
 */
export function buildTimeLabel(now: Date = new Date()): string | null {
  if (!BUILD_TIME) return null
  return `built ${timeLabel(BUILD_TIME)} (${ageLabel(BUILD_TIME, now)})`
}
